import { takeLatest, call, put } from "redux-saga/effects";
import { db } from "../../helpers/firebase/firebaseConfig";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  updateDoc,
} from "firebase/firestore";
import { applyCancellationFine } from "../../helpers/api/cancellationHelper";
import {
  fetchAllCancellationsSuccess,
  fetchAllCancellationsFailure,
  fetchProviderCancellationsSuccess,
  fetchProviderCancellationsFailure,
  applyCancellationFineSuccess,
  applyCancellationFineFailure,
  updateCancellationStatusSuccess,
  updateCancellationStatusFailure,
  fetchCancellationStatisticsSuccess,
  fetchCancellationStatisticsFailure,
} from "./actions";
import {
  FETCH_ALL_CANCELLATIONS_REQUEST,
  FETCH_PROVIDER_CANCELLATIONS_REQUEST,
  APPLY_CANCELLATION_FINE_REQUEST,
  UPDATE_CANCELLATION_STATUS_REQUEST,
  FETCH_CANCELLATION_STATISTICS_REQUEST,
} from "./actionTypes";
import { toast } from "react-toastify";

// Fetch all cancellations from all providers
function* handleFetchAllCancellations() {
  try {
    const cancellationsRef = collection(db, "cancellation_track");
    const querySnapshot = yield call(getDocs, cancellationsRef);

    const cancellations = [];
    querySnapshot.forEach((doc) => {
      cancellations.push({
        id: doc.id,
        ...doc.data(),
      });
    });

    // Sort by cancellation date (newest first)
    cancellations.sort((a, b) => {
      const dateA = new Date(a.cancellation_date || a.created_at);
      const dateB = new Date(b.cancellation_date || b.created_at);
      return dateB - dateA;
    });

    // Enrich with provider details
    const enrichedCancellations = yield call(
      enrichCancellationsWithProviders,
      cancellations
    );

    yield put(fetchAllCancellationsSuccess(enrichedCancellations));
  } catch (error) {
    // console.error("Error fetching all cancellations:", error);
    yield put(fetchAllCancellationsFailure(error.message));
    toast.error("Failed to load cancellations");
  }
}

// Enrich cancellations with provider details
function* enrichCancellationsWithProviders(cancellations) {
  const enriched = yield Promise.all(
    cancellations.map(async (cancellation) => {
      let providerDetails = {
        name: cancellation.service_provider_name || "Unknown",
        email: cancellation.service_provider_email || "",
        phone: cancellation.service_provider_phone || "",
        company: cancellation.service_provider_company || "",
      };

      // Fetch provider details if provider ID exists
      if (cancellation.service_provider_id) {
        try {
          const providerRef = doc(
            db,
            "service_providers",
            cancellation.service_provider_id
          );
          const providerDoc = await getDoc(providerRef);
          if (providerDoc.exists()) {
            const providerData = providerDoc.data();
            providerDetails = {
              name:
                providerData.salon_name ||
                providerData.full_name ||
                providerDetails.name,
              email: providerData.email || providerDetails.email,
              phone: providerData.mobile || providerDetails.phone,
              company: providerData.salon_name || providerDetails.company,
              commercial_register: providerData.commercial_register,
              tax_number: providerData.tax_number,
            };
          }
        } catch (err) {
          console.warn("Error fetching provider details:", err);
        }
      }

      return {
        ...cancellation,
        provider_details: providerDetails,
      };
    })
  );

  return enriched;
}

// Fetch cancellations for a specific provider
function* handleFetchProviderCancellations({ payload: providerId }) {
  try {
    const cancellationsRef = collection(db, "cancellation_track");
    const q = query(
      cancellationsRef,
      where("service_provider_id", "==", providerId)
    );
    const querySnapshot = yield call(getDocs, q);

    const cancellations = [];
    querySnapshot.forEach((doc) => {
      cancellations.push({
        id: doc.id,
        ...doc.data(),
      });
    });

    // Sort by cancellation date (newest first)
    cancellations.sort((a, b) => {
      const dateA = new Date(a.cancellation_date || a.created_at);
      const dateB = new Date(b.cancellation_date || b.created_at);
      return dateB - dateA;
    });

    yield put(fetchProviderCancellationsSuccess(cancellations));
  } catch (error) {
    console.error("Error fetching provider cancellations:", error);
    yield put(fetchProviderCancellationsFailure(error.message));
    toast.error("Failed to load provider cancellations");
  }
}

// Apply fine to cancellation and deduct from provider sale invoices
function* handleApplyCancellationFine({ payload }) {
  try {
    const { cancellationId, fineAmount } = payload;

    // Call the helper function that handles all the deduction logic
    const result = yield call(applyCancellationFine, cancellationId, fineAmount);

    if (!result.success) {
      throw new Error(result.message || "Failed to apply fine");
    }

    // Fetch updated cancellation to get all the deduction details
    const cancellationRef = doc(db, "cancellation_track", cancellationId);
    const updatedCancellationDoc = yield call(getDoc, cancellationRef);

    yield put(
      applyCancellationFineSuccess({
        id: cancellationId,
        ...updatedCancellationDoc.data(),
      })
    );

    toast.success(result.message || "Fine applied successfully");
  } catch (error) {
    console.error("Error applying fine:", error);
    yield put(applyCancellationFineFailure(error.message));
    toast.error(error.message || "Failed to apply fine");
  }
}

// Update cancellation status
function* handleUpdateCancellationStatus({ payload }) {
  try {
    const { cancellationId, newStatus } = payload;

    const cancellationRef = doc(db, "cancellation_track", cancellationId);

    yield call(updateDoc, cancellationRef, {
      status: newStatus,
      updated_at: new Date().toISOString(),
    });

    // Fetch updated document
    const updatedDoc = yield call(getDoc, cancellationRef);

    yield put(
      updateCancellationStatusSuccess({
        id: cancellationId,
        ...updatedDoc.data(),
      })
    );

       toast.success("تم تحديث الحالة بنجاح");
  } catch (error) {
    console.error("Error updating status:", error);
    yield put(updateCancellationStatusFailure(error.message));
    toast.error("Failed to update status");
  }
}

// Fetch cancellation statistics
function* handleFetchCancellationStatistics() {
  try {
    const cancellationsRef = collection(db, "cancellation_track");
    const querySnapshot = yield call(getDocs, cancellationsRef);

    const cancellations = [];
    querySnapshot.forEach((doc) => {
      cancellations.push({
        id: doc.id,
        ...doc.data(),
      });
    });

    // Calculate statistics
    const statistics = {
      total: cancellations.length,
      byProvider: {},
      byStatus: {
        pending: 0,
        confirmed: 0,
        disputed: 0,
      },
      byType: {
        early_cancellation: 0,
        late_cancellation: 0,
        provider_delay: 0,
      },
      byDeductionStatus: {
        pending: 0,
        deducted: 0,
        failed: 0,
      },
      totalFines: 0,
      totalPendingFines: 0,
      totalDeductedFines: 0,
    };

    cancellations.forEach((cancellation) => {
      // By provider
      const providerId = cancellation.service_provider_id || "unknown";
      const providerName =
        cancellation.service_provider_name || "Unknown Provider";

      if (!statistics.byProvider[providerId]) {
        statistics.byProvider[providerId] = {
          name: providerName,
          count: 0,
          totalFines: 0,
          pendingFines: 0,
          deductedFines: 0,
        };
      }

      statistics.byProvider[providerId].count++;

      const fine = cancellation.total_fine || 0;
      statistics.byProvider[providerId].totalFines += fine;

      if (cancellation.deduction_status === "deducted") {
        statistics.byProvider[providerId].deductedFines += fine;
      } else if (["pending", "pending_settlement", "partial"].includes(cancellation.deduction_status)) {
        statistics.byProvider[providerId].pendingFines += fine;
      }

      // By status
      if (cancellation.status) {
        statistics.byStatus[cancellation.status] =
          (statistics.byStatus[cancellation.status] || 0) + 1;
      }

      // By type
      if (cancellation.cancellation_type) {
        statistics.byType[cancellation.cancellation_type] =
          (statistics.byType[cancellation.cancellation_type] || 0) + 1;
      }

      // By deduction status
      if (cancellation.deduction_status) {
        statistics.byDeductionStatus[cancellation.deduction_status] =
          (statistics.byDeductionStatus[cancellation.deduction_status] || 0) +
          1;
      }

      // Total fines
      statistics.totalFines += fine;
      if (cancellation.deduction_status === "deducted") {
        statistics.totalDeductedFines += fine;
      } else if (["pending", "pending_settlement", "partial"].includes(cancellation.deduction_status)) {
        statistics.totalPendingFines += fine;
      }
    });

    yield put(fetchCancellationStatisticsSuccess(statistics));
  } catch (error) {
    console.error("Error fetching statistics:", error);
    yield put(fetchCancellationStatisticsFailure(error.message));
    toast.error("Failed to load statistics");
  }
}

export function* watchCancellationsSaga() {
  yield takeLatest(
    FETCH_ALL_CANCELLATIONS_REQUEST,
    handleFetchAllCancellations
  );
  yield takeLatest(
    FETCH_PROVIDER_CANCELLATIONS_REQUEST,
    handleFetchProviderCancellations
  );
  yield takeLatest(APPLY_CANCELLATION_FINE_REQUEST, handleApplyCancellationFine);
  yield takeLatest(
    UPDATE_CANCELLATION_STATUS_REQUEST,
    handleUpdateCancellationStatus
  );
  yield takeLatest(
    FETCH_CANCELLATION_STATISTICS_REQUEST,
    handleFetchCancellationStatistics
  );
}
